/**
 * Product Service
 * API calls to Products Lambda
 */

import { apiConfig } from '../config/api';

// Debug: Log the actual API URL being used
console.log('🔍 ProductService BASE_URL:', apiConfig.productsApi);
console.log('🔍 ProductService apiConfig:', apiConfig);

const BASE_URL = apiConfig.productsApi;

export interface Product {
  id: string;
  brandId: string;
  name: string;
  sku: string;
  category: string;
  price: number;
  currency: string;
  images: string[];
  stockQuantity?: number;
  isActive: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductsResponse {
  success: boolean;
  data: {
    products: Product[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
}

export interface GetProductsParams {
  brandId: string;
  limit?: number;
  offset?: number;
  sku?: string;
  name?: string;
  category?: string;
}

export class ProductService {
  /**
   * Get products with filters and pagination
   */
  static async getProducts(params: GetProductsParams): Promise<ProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      // Required
      queryParams.append('brandId', params.brandId);
      
      // Optional - Pagination
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      
      // Optional - Filters
      if (params.sku) queryParams.append('sku', params.sku);
      if (params.name) queryParams.append('name', params.name);
      if (params.category) queryParams.append('category', params.category);

      const url = `${BASE_URL}/api/v1/products?${queryParams.toString()}`;
      console.log('🔍 Fetching products:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('✅ Products Response:', data);
      
      if (!data.success || !data.data) {
        throw new Error('Invalid API response');
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch products:', error);
      // Return empty response with mock structure
      return {
        success: false,
        data: {
          products: [],
          pagination: {
            total: 0,
            limit: params.limit || 20,
            offset: params.offset || 0,
            hasMore: false,
          },
        },
      };
    }
  }

  /**
   * Get product by ID
   */
  static async getProductById(productId: string): Promise<Product | null> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/products/${productId}`);
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.product) {
        throw new Error('Product not found');
      }

      return data.data.product;
    } catch (error) {
      console.error('Failed to fetch product:', error);
      return null;
    }
  }

  /**
   * Get unique categories for a brand
   */
  static async getCategories(brandId: string): Promise<{ name: string; count: number }[]> {
    try {
      const url = `${BASE_URL}/api/v1/products/categories?brandId=${brandId}`;
      console.log('🔍 ProductService.getCategories - Full URL:', url);
      console.log('🔍 ProductService.getCategories - BASE_URL:', BASE_URL);
      console.log('🔍 ProductService.getCategories - apiConfig:', apiConfig);
      const response = await fetch(url);
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.categories) {
        return [];
      }

      return data.data.categories;
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      return [];
    }
  }

  /**
   * Sync products from brand's XML feed
   */
  static async syncProductsFromBrand(
    brandId: string,
    mappingName: string = 'default',
    limitProducts: number = 100,
    forceRefresh: boolean = false,
    getAllProducts: boolean = false
  ): Promise<{
    success: boolean;
    data: {
      syncResult: {
        success: boolean;
        brandId: string;
        brandName: string;
        totalFetched: number;
        totalSynced: number;
        totalUpdated: number;
        totalCreated: number;
        errors: string[];
        syncedAt: Date;
      };
    };
    message: string;
  }> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/products/sync/${brandId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mappingName,
          limitProducts,
          forceRefresh,
          getAllProducts,
        }),
      });

      const data = await response.json();

      if (!data.success || !data.data) {
        throw new Error(data.message || 'Ürün senkronizasyonu başarısız');
      }

      return data;
    } catch (error) {
      console.error('Failed to sync products:', error);
      throw error;
    }
  }
}

